import { supabase } from '../lib/supabase';
import { notificationService } from './notificationService';

/**
 * Group Counseling Service
 * Handles one-to-many group counseling sessions
 */
export const groupCounselingService = {
  // ─── Institute: Create / Manage Sessions ────────────────────────────────────

  async createSession(sessionData) {
    const jitsiRoomName = `group-counseling-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`;
    const { data, error } = await supabase
      ?.from('group_counseling_sessions')
      ?.insert({
        institute_id: sessionData?.instituteId,
        title: sessionData?.title,
        description: sessionData?.description,
        topic: sessionData?.topic,
        scheduled_at: sessionData?.scheduledAt,
        duration_minutes: sessionData?.durationMinutes || 45,
        max_participants: sessionData?.maxParticipants || 20,
        status: 'scheduled',
        allow_late_entry: sessionData?.allowLateEntry !== false,
        late_entry_minutes: sessionData?.lateEntryMinutes || 10,
        jitsi_room_name: jitsiRoomName,
        meeting_url: `/group-counseling/${jitsiRoomName}`
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  async updateSession(sessionId, updates) {
    const { data, error } = await supabase
      ?.from('group_counseling_sessions')
      ?.update({
        title: updates?.title,
        description: updates?.description,
        topic: updates?.topic,
        scheduled_at: updates?.scheduledAt,
        duration_minutes: updates?.durationMinutes,
        max_participants: updates?.maxParticipants,
        allow_late_entry: updates?.allowLateEntry,
        late_entry_minutes: updates?.lateEntryMinutes,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', sessionId)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  async updateSessionStatus(sessionId, status) {
    const { data, error } = await supabase
      ?.from('group_counseling_sessions')
      ?.update({ status, updated_at: new Date()?.toISOString() })
      ?.eq('id', sessionId)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  async deleteSession(sessionId) {
    const { error } = await supabase
      ?.from('group_counseling_sessions')
      ?.delete()
      ?.eq('id', sessionId);

    if (error) throw error;
    return true;
  },

  async getInstituteSessions(instituteId) {
    const { data, error } = await supabase
      ?.from('group_counseling_sessions')
      ?.select(`
        *,
        registrations:group_session_registrations(
          id, student_id, status, registered_at,
          student:student_id(id, name, email)
        )
      `)
      ?.eq('institute_id', instituteId)
      ?.order('scheduled_at', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async getSessionRegistrations(sessionId) {
    const { data, error } = await supabase
      ?.from('group_session_registrations')
      ?.select(`
        *,
        student:student_id(id, name, email)
      `)
      ?.eq('session_id', sessionId)
      ?.eq('status', 'registered')
      ?.order('registered_at', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  // ─── Student: Browse / Register ─────────────────────────────────────────────

  async getUpcomingSessions(filters = {}) {
    const now = new Date()?.toISOString();
    let query = supabase
      ?.from('group_counseling_sessions')
      ?.select(`
        *,
        institute:institute_id(id, name, email, institute_profiles(institute_name, logo_url)),
        registrations:group_session_registrations(id, student_id, status)
      `)
      ?.in('status', ['scheduled', 'ongoing'])
      ?.order('scheduled_at', { ascending: true });

    if (filters?.instituteId) {
      query = query?.eq('institute_id', filters?.instituteId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async getStudentRegistrations(studentId) {
    const { data, error } = await supabase
      ?.from('group_session_registrations')
      ?.select(`
        *,
        session:session_id(
          *,
          institute:institute_id(id, name, email, institute_profiles(institute_name, logo_url))
        )
      `)
      ?.eq('student_id', studentId)
      ?.eq('status', 'registered')
      ?.order('registered_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async isStudentRegistered(sessionId, studentId) {
    const { data, error } = await supabase
      ?.from('group_session_registrations')
      ?.select('id, status')
      ?.eq('session_id', sessionId)
      ?.eq('student_id', studentId)
      ?.maybeSingle();

    if (error) return false;
    return data?.status === 'registered';
  },

  async registerStudent(sessionId, studentId) {
    // Check capacity
    const { data: session, error: sessionError } = await supabase
      ?.from('group_counseling_sessions')
      ?.select('*, institute:institute_id(id, name, email, institute_profiles(institute_name))')
      ?.eq('id', sessionId)
      ?.single();

    if (sessionError) throw sessionError;

    const { count } = await supabase
      ?.from('group_session_registrations')
      ?.select('id', { count: 'exact', head: true })
      ?.eq('session_id', sessionId)
      ?.eq('status', 'registered');

    if (count >= session?.max_participants) {
      throw new Error('This session is full. No more registrations available.');
    }

    const { data, error } = await supabase
      ?.from('group_session_registrations')
      ?.insert({
        session_id: sessionId,
        student_id: studentId,
        status: 'registered'
      })
      ?.select()
      ?.single();

    if (error) throw error;

    // Send confirmation email
    try {
      const { data: student } = await supabase
        ?.from('users')
        ?.select('name, email')
        ?.eq('id', studentId)
        ?.single();

      if (student?.email) {
        const instituteName =
          session?.institute?.institute_profiles?.[0]?.institute_name ||
          session?.institute?.name ||
          'Institute';

        await groupCounselingService?.sendRegistrationEmail({
          studentName: student?.name,
          studentEmail: student?.email,
          sessionTitle: session?.title,
          instituteName,
          scheduledAt: session?.scheduled_at,
          durationMinutes: session?.duration_minutes,
          topic: session?.topic,
          sessionId
        });

        // Mark email as sent
        await supabase
          ?.from('group_session_registrations')
          ?.update({ email_sent: true })
          ?.eq('id', data?.id);
      }
    } catch (emailError) {
      console.error('Error sending registration email:', emailError);
    }

    return data;
  },

  async cancelRegistration(sessionId, studentId) {
    const { error } = await supabase
      ?.from('group_session_registrations')
      ?.update({ status: 'cancelled' })
      ?.eq('session_id', sessionId)
      ?.eq('student_id', studentId);

    if (error) throw error;
    return true;
  },

  // ─── Late Entry Check ────────────────────────────────────────────────────────

  canStudentJoin(session) {
    if (!session) return { canJoin: false, reason: 'Session not found' };

    const now = new Date();
    const sessionStart = new Date(session?.scheduled_at);
    const sessionEnd = new Date(sessionStart?.getTime() + session?.duration_minutes * 60 * 1000);
    const minutesSinceStart = (now - sessionStart) / 60000;

    if (session?.status === 'scheduled' && now < sessionStart) {
      return { canJoin: true, reason: 'Session not started yet — you can join early' };
    }

    if (session?.status === 'ongoing') {
      if (session?.allow_late_entry && minutesSinceStart <= session?.late_entry_minutes) {
        return {
          canJoin: true,
          reason: `Late entry allowed (within ${session?.late_entry_minutes} minutes of start)`
        };
      }
      if (!session?.allow_late_entry && minutesSinceStart > 0) {
        return { canJoin: false, reason: 'Late entry is not allowed for this session' };
      }
      if (minutesSinceStart > session?.late_entry_minutes) {
        return {
          canJoin: false,
          reason: `Late entry window (${session?.late_entry_minutes} min) has passed`
        };
      }
      return { canJoin: true, reason: 'Session is live' };
    }

    if (session?.status === 'completed' || now > sessionEnd) {
      return { canJoin: false, reason: 'Session has ended' };
    }

    if (session?.status === 'cancelled') {
      return { canJoin: false, reason: 'Session has been cancelled' };
    }

    return { canJoin: true, reason: 'Session is available' };
  },

  // ─── Email ───────────────────────────────────────────────────────────────────

  async sendRegistrationEmail({
    studentName,
    studentEmail,
    sessionTitle,
    instituteName,
    scheduledAt,
    durationMinutes,
    topic,
    sessionId
  }) {
    const sessionDate = new Date(scheduledAt);
    const formattedDate = sessionDate?.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const formattedTime = sessionDate?.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

    const html = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f8fafc;">
        <div style="background: white; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 6px rgba(0,0,0,0.07);">
          
          <!-- Header -->
          <div style="background: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%); padding: 32px 40px; text-align: center;">
            <h1 style="color: white; margin: 0; font-size: 28px; font-weight: 700;">Studyexpo</h1>
            <p style="color: rgba(255,255,255,0.85); margin: 8px 0 0 0; font-size: 14px;">Virtual Education Fair Platform</p>
          </div>

          <!-- Success Banner -->
          <div style="background: #eff6ff; border-bottom: 3px solid #2563eb; padding: 24px 40px; text-align: center;">
            <div style="display: inline-block; background: #2563eb; border-radius: 50%; width: 56px; height: 56px; line-height: 56px; text-align: center; margin-bottom: 12px;">
              <span style="color: white; font-size: 28px;">✓</span>
            </div>
            <h2 style="color: #1d4ed8; margin: 0; font-size: 22px; font-weight: 700;">Registration Confirmed!</h2>
            <p style="color: #1e40af; margin: 8px 0 0 0; font-size: 15px;">You are registered for a Group Counseling Session</p>
          </div>

          <!-- Body -->
          <div style="padding: 32px 40px;">
            <p style="color: #334155; font-size: 16px; margin: 0 0 16px 0;">Hi <strong>${studentName || 'there'}</strong>,</p>
            <p style="color: #475569; font-size: 15px; line-height: 1.6; margin: 0 0 24px 0;">
              Your registration for the group counseling session has been confirmed. Here are your session details:
            </p>

            <!-- Session Details Card -->
            <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 24px; margin-bottom: 24px;">
              <h3 style="color: #1e293b; font-size: 18px; font-weight: 700; margin: 0 0 16px 0;">${sessionTitle}</h3>
              <table style="width: 100%; border-collapse: collapse;">
                <tr>
                  <td style="padding: 8px 0; color: #64748b; font-size: 14px; width: 40%;">🏛️ Institute</td>
                  <td style="padding: 8px 0; color: #1e293b; font-size: 14px; font-weight: 600;">${instituteName}</td>
                </tr>
                ${topic ? `<tr>
                  <td style="padding: 8px 0; color: #64748b; font-size: 14px;">📚 Topic</td>
                  <td style="padding: 8px 0; color: #1e293b; font-size: 14px;">${topic}</td>
                </tr>` : ''}
                <tr>
                  <td style="padding: 8px 0; color: #64748b; font-size: 14px;">📅 Date</td>
                  <td style="padding: 8px 0; color: #1e293b; font-size: 14px; font-weight: 600;">${formattedDate}</td>
                </tr>
                <tr>
                  <td style="padding: 8px 0; color: #64748b; font-size: 14px;">🕐 Time</td>
                  <td style="padding: 8px 0; color: #1e293b; font-size: 14px; font-weight: 600;">${formattedTime}</td>
                </tr>
                <tr>
                  <td style="padding: 8px 0; color: #64748b; font-size: 14px;">⏱️ Duration</td>
                  <td style="padding: 8px 0; color: #1e293b; font-size: 14px;">${durationMinutes} minutes</td>
                </tr>
                <tr>
                  <td style="padding: 8px 0; color: #64748b; font-size: 14px;">🎥 Format</td>
                  <td style="padding: 8px 0; color: #1e293b; font-size: 14px;">Group Video Session (One-to-Many)</td>
                </tr>
              </table>
            </div>

            <!-- How to Join -->
            <div style="background: #fffbeb; border: 1px solid #fde68a; border-radius: 10px; padding: 20px; margin-bottom: 24px;">
              <h3 style="color: #92400e; font-size: 14px; font-weight: 600; margin: 0 0 12px 0;">📋 How to Join the Session:</h3>
              <ol style="color: #78350f; font-size: 13px; line-height: 1.8; margin: 0; padding-left: 20px;">
                <li>Login to your Studyexpo student account</li>
                <li>Go to <strong>Group Counseling</strong> in the navigation</li>
                <li>Find this session and click <strong>Join Session</strong></li>
                <li>You will be connected to the live group video room</li>
              </ol>
            </div>

            <!-- CTA -->
            <div style="text-align: center; margin-bottom: 24px;">
              <a href="https://studyexpo.online/group-counseling-sessions"
                 style="display: inline-block; background: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%); color: white; text-decoration: none; padding: 14px 36px; border-radius: 8px; font-size: 16px; font-weight: 600;">
                View My Sessions →
              </a>
            </div>

            <p style="color: #64748b; font-size: 13px; line-height: 1.6; margin: 0;">
              If you can no longer attend, please cancel your registration from your dashboard so others can join.
            </p>
          </div>

          <!-- Footer -->
          <div style="background: #f1f5f9; padding: 20px 40px; text-align: center; border-top: 1px solid #e2e8f0;">
            <p style="color: #94a3b8; font-size: 12px; margin: 0;">© 2026 Studyexpo. All rights reserved.</p>
          </div>
        </div>
      </div>
    `;

    await notificationService?.sendEmail({
      to: studentEmail,
      subject: `✅ Registered: ${sessionTitle} — Group Counseling Session`,
      html,
      type: 'group_session_registration'
    });
  }
};
